import { ChangeDetectionStrategy, Component, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { CartRecoverService, RestaurantService } from 'src/app/core';
import { ModalController } from 'src/app/core/overlay';
import { MenuProduct } from 'src/app/shared/model/MenuProduct';
import { Extra } from 'src/app/shared/model/Extra';
import { NavService } from 'src/app/core/services/nav.service';
import { ShoppingCartService } from 'src/app/core/services/shoppingCart.service';
import { environment } from 'src/environments/environment';
import { ViewerModalComponent } from 'src/app/shared/components/image-viewer/viewer-modal.component';
import { KeenTrackingService } from 'src/app/core/services/keenTracking.service';
import { OrderFlowTraceService } from 'src/app/core/services/telemetry/order-flow-trace.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UtilsService } from 'src/app/core/services/utils.service';

/** De onde partiu a abertura do item — dimensão da telemetria, não do roteamento. */
export type ItemOpenOrigin = 'card' | 'photo_action';

@Component({
	selector: 'card',
	templateUrl: './card.page.html',
	styleUrls: ['./card.page.scss'],
	// OnPush (Caso 16): sem isto, CADA ciclo de CD do app re-checava TODOS os
	// cards já renderizados — o custo de boot crescia O(N) por lote do render
	// progressivo. Seguro: inputs têm referência estável, nada muta item.*
	// pós-render (pipeline do restaurant.service roda antes do render) e o
	// TranslatePipe do ngx-translate faz markForCheck sozinho.
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardPage implements OnInit, OnChanges {
	@Input() item: MenuProduct;
	@Input() extrasList: Extra[];
	@Input() isFeatured: boolean = false;
	@Input() newHomePedirDelivery: boolean;
	fromPrice: any = false;
    allRequiredExtrasOutOfStock: boolean = false;


	imgURL = environment.imgURL;
	defaultImage = 'assets/images/sem_produto.png';

	public newNepPizzas = false;
	public comboFromNep = false;
	public showOldPrice = false;

	// Nós do card que existiam só como gancho de seletor `:has()` viraram estado
	// explícito (Caso 17). Campos, não getters: OnPush + input imutável, então
	// calcular uma vez no ngOnInit basta e nada roda por change detection.
	//
	// `hasImageSlot` decide se o `.image-container` sequer entra no DOM. Antes o
	// container e um `.image-placeholder` vazio eram sempre criados e escondidos
	// por `.image-container:has(.image-placeholder){display:none}` — 2 nós por
	// produto sem foto (87% dos itens numa distribuidora) só para alimentar um
	// seletor. BÔNUS: `:has()` só existe no Safari 15.4+, então abaixo disso a
	// regra inteira era descartada e a caixa de 120px aparecia vazia no card.
	public hasImageSlot = false;
	// `hasTextBlock` idem para o `.text-block`, que era criado VAZIO sempre que o
	// item tinha estoque — o `*ngIf` dele olhava estoque, não descrição.
	public hasTextBlock = false;
	// Mesmo problema no layout LEGADO (Caso 17-B, 89,6% das lojas): lá o
	// `<ion-label class="text-wrap"><span [innerHTML]></span></ion-label>` da
	// descrição não tinha `*ngIf` NENHUM — nascia em todo card, com ou sem
	// descrição. São 2 nós (e um custom element do Ionic para "upgradar") em
	// 1.959 dos 1.960 produtos da loja 8466. Aqui NÃO entra estoque na conta: o
	// legado mostra a descrição mesmo em item indisponível, e a régua é preservar
	// o comportamento, não uniformizá-lo com o layout novo.
	public hasDescription = false;
	// UA não muda durante a sessão: avaliar uma única vez (era chamado 2x por card por change detection). Guard SSR.
	public readonly isInstagramUA = typeof navigator !== 'undefined' && navigator.userAgent.indexOf('Instagram') > -1;

	// Cache da descrição processada: getSafeHtml no template retornava um SafeHtml NOVO a cada
	// change detection, forçando o Angular a re-aplicar [innerHTML] (re-parse de HTML + relayout)
	// em todos os cards com descrição a cada tick — principal causa de travamento em cardápios grandes.
	private descriptionCacheKey: any = null;
	private descriptionCacheHtml: any = null;
	private descriptionCacheText: string = '';
	private descriptionCacheReady = false;

	// Getters estáveis para o template: só recomputam se item.description mudar.
	get descriptionHtml(): any {
		this.updateDescriptionCache();
		return this.descriptionCacheHtml;
	}

	get descriptionText(): string {
		this.updateDescriptionCache();
		return this.descriptionCacheText;
	}

	private updateDescriptionCache() {
		const desc = this.item ? this.item.description : undefined;
		if (this.descriptionCacheReady && desc === this.descriptionCacheKey) return;
		this.descriptionCacheKey = desc;
		this.descriptionCacheHtml = this.getSafeHtml(desc);
		this.descriptionCacheText = desc ? this.removeHtmlTags(desc) : '';
		this.descriptionCacheReady = true;
	}

	constructor(
		private rService: RestaurantService,
		private navService: NavService,
		public modalController: ModalController,
		private cartService: ShoppingCartService,
		private keen: KeenTrackingService,
		private cartRecoverService: CartRecoverService,
		private utils: UtilsService,
		private orderFlowTrace: OrderFlowTraceService,
        private sanitizer: DomSanitizer
	) { }

	isShortTitle(): boolean {
		const name = this.item?.name;
		return name != null && name.length <= 22;
	}

	getShowOldPrice(): boolean {
		if (!this.item.oldPrice) return false;
		const newNepPizzas = this.rService.restaurant.info.modules.new_nep_pizzas;
		if (this.item.type === 'pizza') {
		  return !newNepPizzas || (newNepPizzas && this.item.priceBehavior === 'incremental');
		}
		if (this.item.type === 'combo') {
		  return !this.item.fromNEP;
		}
		return true;
	  }

	ngOnInit() {
		this.productsWithExtraHasStock();
		this.showOldPrice = this.getShowOldPrice();
		this.computeNodeFlags();
	}

	// O card é REAPROVEITADO com outro `item`: o `trackById` do menu-list casa
	// pelo id, e busca/`reloadRestaurant` substituem o menu por objetos novos.
	// Nesse caminho o `ngOnInit` não roda de novo — sem isto os flags abaixo
	// ficariam com o estado do item ANTIGO (ex.: produto que saiu de estoque
	// manteria o `.text-block`). Antes dos flags o template lia `item.*` ao vivo,
	// então este é o preço de tê-los tirado do binding. `firstChange` fica de
	// fora porque o `ngOnInit`, logo em seguida, já calcula.
	ngOnChanges(changes: SimpleChanges) {
		const item = changes['item'];
		const featured = changes['isFeatured'];
		if ((item && !item.firstChange) || (featured && !featured.firstChange)) {
			this.computeNodeFlags();
		}
	}

	// Mesmas condições que os `*ngIf` do template usavam para decidir o
	// CONTEÚDO; agora decidem também se o container existe.
	private computeNodeFlags() {
		this.hasImageSlot = !!this.item?.image || this.isFeatured;
		this.hasTextBlock = this.item?.hasStock !== false
			&& !this.allRequiredExtrasOutOfStock
			&& !!this.item?.description;
		this.hasDescription = !!this.item?.description;
	}

	// Item que o card não deixa abrir também não pode ser pedido pela foto.
	get isUnavailable(): boolean {
		return this.item?.hasStock === false || this.allRequiredExtrasOutOfStock;
	}

	async openViewer(item) {
		const modal = await this.modalController.create({
			component: ViewerModalComponent,
			componentProps: {
				src: this.imgURL+'/products/'+item.image+'/lg_'+item.image+'.jpg',
				scheme: 'dark',
				swipeToClose: false,
				title: item.name,
				text: item.description ? this.removeHtmlTags(item.description) : '',
				// Chaves, não texto pronto: o `| translate` do viewer é reativo e se
				// corrige sozinho se o pacote de idioma ainda não tiver chegado —
				// `instant()` aqui congelaria a CHAVE CRUA na tela do consumidor.
				actionLabel: 'Want_this_item',
				actionDisabled: this.isUnavailable,
				actionDisabledLabel: 'Unavailable_now',
				// Contexto só de telemetria: quem é o item e de onde a foto abriu.
				// Ver ClientItemPhoto* em order-flow-steps.ts.
				tracking: {
					itemId: item.id,
					itemType: item.type,
					price: item.price || item.fromPrice || 0,
					opener: 'menu-card',
				},
			},
			cssClass: 'ion-img-viewer',
			keyboardClose: true,
			showBackdrop: true
		});
	  
		await modal.present();

		// O botão só fecha o modal; a seleção acontece aqui, com a tela já livre —
		// e pelo MESMO caminho de tocar no card, para não duplicar telemetria nem
		// criar uma segunda regra de destino por tipo de item.
		modal.onDidDismiss().then(({ role }) => {
			if (role === ViewerModalComponent.SELECT_ROLE) this.goToItemPage(item, 'photo_action');
		});

		const topModal = await this.modalController.getTop();
		const element = topModal.querySelector('.ion-img-viewer .mp-text');
	  
		if (element) {
		  const ionText = element as HTMLElement;
		  ionText.setAttribute('tabindex', '0');
		  ionText.focus();
		}

		// Fechar ao tocar fora da imagem. Os seletores são os do visualizador DESIONIZADO
		// (viewer-modal.component.html): ion-content → .mp-content, ion-text → .mp-text,
		// ion-button → .mp-button. Com os nomes antigos o addEventListener não era sequer
		// registrado — o toque fora da imagem deixou de fechar o visualizador.
		//
		// `.viewer-action` entrou na lista porque o botão "Quero esse" passou a
		// flutuar DENTRO do conteúdo: sem isso o clique nele fecharia o modal
		// por este atalho, correndo com o dismiss de seleção do próprio botão.
		topModal.querySelector('.mp-content')?.addEventListener('click', (ev) => {
			if ((ev.target as HTMLElement).closest('img, .mp-text, .mp-button, .viewer-action')) return;
			modal.dismiss();
		});
	  
		return modal;
	  }
	  
    convertToSafeHtml(rawHtml: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(rawHtml);
    }

	isInstagram() {
		var ua = navigator.userAgent;
		return (ua.indexOf('Instagram') > -1) ? true : false;
	}

	removeHtmlTags(str: string) {
		return this.utils.removeTags(str);
	}

    getSafeHtml(value: any) {
		try {
			const parsedValue = JSON.parse(value);
			const sanitizedValue = this.utils.sanitizeAndFormatText(parsedValue);
			return this.convertToSafeHtml(sanitizedValue);
		} catch (error) {
			const sanitizedValue = this.utils.sanitizeAndFormatText(value);
			return this.removeHtmlTags(sanitizedValue);
		}
	}

	/**
	 * @param via de onde veio a abertura: o toque no card (default) ou o botão
	 *            "Quero esse" da foto ampliada. É o campo que torna a adoção do
	 *            botão ATRIBUÍVEL: com ele, todo evento a jusante no mesmo
	 *            `correlation_id` (adicionou ao carrinho? pediu?) pode ser lido
	 *            por origem. Sem ele, só dá para contar cliques — e clique não é
	 *            venda. Ver TELEMETRIA-QUERO-ESSE.md.
	 */
	goToItemPage(item: MenuProduct, via: ItemOpenOrigin = 'card') {
		this.keen.event('goToItemPage', item);

		// `target_page` fica de fora de propósito: a rota de destino é decidida
		// abaixo por três ramos de módulo (pizza/nep/intermediário/extras) e
		// recalculá-la aqui só para o evento duplicaria a regra — duas cópias que
		// divergem no primeiro módulo novo. `is_combo` já separa o que importa.
		this.orderFlowTrace.emit('ClientProductOpened', {
			in: {
				item_id: item.id,
				item_type: item.type,
				price: item.price || item.fromPrice || 0,
				extras_count: (item.extras || []).length,
				opened_via: via,
			},
			out: { is_combo: item.type === 'combo' },
		}, 'card.goToItemPage');

		gtag('event', 'view_item', {
			'currency': this.rService.restaurant.info.currency,
			'value': item.price || item.fromPrice || 0,
			// Parâmetro extra (aditivo — o evento padrão do GA4 continua válido):
			// o order-flow só enxerga a coorte, e o GA4 cobre a base inteira. Para
			// virar dimensão em relatório precisa ser registrado como dimensão
			// personalizada no painel do GA4.
			'opened_via': via,
			'items': [{
				'item_id': item.id,
				'item_name': item.name,
				'item_category': item.type,
				'price': item.price || item.fromPrice || 0,
			}]
		});

		this.cartRecoverService.updateTimestamp();

		if(item.type === 'combo') {
			this.goToPizzaPage(item, true);
		}else if(item.type === 'pizza') {
			this.goToPizzaPage(item);
		}else {
			this.goToExtrasPage(item.extras || []);
		}
	}

	async goToPizzaPage(item, isCombo=false) {
		let pizzaParams = {
			'product': item,
			'isCombo': isCombo,
			'menu': this.rService.restaurant.menu
		};

		// TODO: remover e deixar apenas o intermediário
		if(this.rService.restaurant.info.modules.new_nep_pizzas) {
			// TODO: remover depois que as mudanças do CRM estiverem em prd
			if (this.rService.restaurant.info.modules.new_complements_pedir_delivery) {
				this.navService.push('pizza-intermediary', pizzaParams);
				return;
			}
			this.navService.push('pizza-nep', pizzaParams);
		} else {
			this.navService.push('pizza', pizzaParams);
		}
	}

	async goToExtrasPage(itemExtras: number[]) {
        const orderLevelExtrasEnabled = !!this.rService.restaurant?.info?.modules?.order_level_extras;
        let list;
        if (orderLevelExtrasEnabled) {
            list = itemExtras.map(extraID => {
                return this.rService.restaurant.menu.extras.filter((menuExtra) => menuExtra.id == extraID && !menuExtra.isOrderLevel)[0] ?? null;
            }).filter(extra => extra?.options.length > 0);
        } else {
            list = itemExtras.map(extraID => {
                return this.rService.restaurant.menu.extras.filter((menuExtra) => menuExtra.id == extraID)[0] ?? null;
            }).filter(extra => extra?.options.length > 0);
        }

		let selectedItem = { ...this.item};

		const isNewComplements = !!(this.rService.restaurant?.info?.modules?.new_complements_pedir_delivery);

		if (selectedItem.type == 'general' && list.length == 0 && !isNewComplements) {
			selectedItem.extras = [];
			selectedItem.qty = 1;
			this.cartService.addProductToCart(selectedItem);
			this.goToShoppingCart();
		} else {
			// Clone necessário (a tela de extras muta seleções), mas o
			// structuredClone nativo é bem mais barato que serializar JSON.
			let extraParams = {
				'extras': typeof structuredClone === 'function' ? structuredClone(list) : JSON.parse(JSON.stringify(list)),
				'item': selectedItem,
			};
			const route = 'extra';
			this.navService.push(route, extraParams)
		}
	}

	async goToShoppingCart() {
		this.navService.push('shopping-cart');
	}

	showStockPausedItems() {
		const info = this.rService.restaurant.info;
		if (info.modules?.ingredient_inventory) {
			return info.supply_stock_config?.display_paused_items_as_sold_out ?? false;
		}
		return info.stock_configuration?.display_paused_items_as_sold_out ?? false;
	}


    productsWithExtraHasStock() {
        const { extras: availableExtras } = this.rService.restaurant.menu;
        const { extras: itemExtras } = this.item;

        if (!itemExtras?.length) {
            return;
        }

        const extrasRequired = availableExtras.filter((extra) =>
            itemExtras.includes(extra.id) && (extra.required === 1 || extra.qty_min >=1) && extra.options.length > 0
        );

        if (!extrasRequired.length) {
			if(!availableExtras.find(extra => itemExtras.includes(extra.id))){
				this.allRequiredExtrasOutOfStock = !this.item.price && !this.item.fromPrice;
			}

            return;
        }

        this.allRequiredExtrasOutOfStock = extrasRequired.every(extra =>     
            extra.options.every(option =>
                option.stock?.id && option.hasStock === false
            )
        );
    }
}
